import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { patientsApi } from '@/api/patients';
import { surveysApi, Survey } from '@/api/surveys';
import patientFormImg from '@/assets/images/patient-form.webp';
import AssignConfirmStep from '@/components/assignments/steps/AssignConfirmStep';
import AssignPatientSelectStep from '@/components/assignments/steps/AssignPatientSelectStep';
import AssignSuccessStep from '@/components/assignments/steps/AssignSuccessStep';
import AssignSurveyConflictStep from '@/components/assignments/steps/AssignSurveyConflictStep';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { useDebounce } from '@/hooks/use-debounce';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import {
    EntitlementBlockedError,
    isEntitlementBlockedError,
} from '@/lib/entitlement-blocked-error';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { getSharableSurveyText } from '@/pages/surveys/utils/getSurveyText';
import { Patient } from '@/types/patient';
import { toTitleCase } from '@/utils/toTitleCase';
import type { AssignPatientListItem } from '@/components/assignments/steps/AssignPatientSelectStep';

const PATIENT_FORM_IMAGE = {
    src: patientFormImg,
    width: 960,
    height: 909,
};

interface SurveyAssignWizardProps {
    survey: Survey;
    preSelectedPatient?: Patient;
    onClose: () => void;
    allowPatientSelection?: boolean;
    onAssigned?: (surveyId: string) => void;
}

type SurveyResponsePayload = {
    unlock_token?: string;
    unlock_token_expires_at?: string;
    data?: {
        unlock_token?: string;
        unlock_token_expires_at?: string;
    };
};

export default function SurveyAssignWizard({
    survey,
    preSelectedPatient,
    onClose,
    allowPatientSelection = true,
    onAssigned,
}: SurveyAssignWizardProps) {
    const [step, setStep] = useState<1 | 2 | 3 | 4>(
        preSelectedPatient || !allowPatientSelection ? 2 : 1,
    );
    const [selectedPatient, setSelectedPatient] = useState<Patient | null>(
        preSelectedPatient || null,
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [surveyResponse, setSurveyResponse] = useState<SurveyResponsePayload | null>(null);
    const sendSurveyAccess = useFeatureEntitlement('surveys.send');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const normalizedSearchQuery = debouncedSearchQuery.trim();

    const canSelectPatient = allowPatientSelection;
    const queryClient = useQueryClient();

    const {
        data: patients = [],
        isLoading: isLoadingPatients,
        isFetching: isFetchingPatients,
    } = useQuery({
        queryKey: ['patients', 'all', normalizedSearchQuery],
        queryFn: () => patientsApi.listAll({ q: normalizedSearchQuery || undefined }),
        enabled: canSelectPatient,
    });

    const { mutate: sendSurvey, isPending } = useMutation({
        mutationFn: async ({ confirm = false }: { confirm?: boolean }) => {
            if (sendSurveyAccess.shouldBlock) {
                throw new EntitlementBlockedError(
                    getEntitlementBlockedMessage(sendSurveyAccess.decision),
                );
            }

            if (!selectedPatient) return;
            return await surveysApi.send(selectedPatient.id, survey.id, confirm);
        },
        onSuccess: (data) => {
            setSurveyResponse(data ?? null);
            setStep(4);
            if (onAssigned) onAssigned(survey.id);
            queryClient.invalidateQueries({
                queryKey: ['survey-reports'],
            });
            toast.success(`Formulário atribuído com sucesso para ${selectedPatient?.full_name}`);
        },
        onError: (error: unknown) => {
            const status = isAxiosError(error) ? error.response?.status : undefined;
            const message = isAxiosError(error)
                ? ((error.response?.data as { message?: string } | undefined)?.message ?? '')
                : '';

            if (
                status === 409 ||
                message.includes('already has a survey') ||
                message.includes('já possui')
            ) {
                setStep(3);
            } else if (isEntitlementBlockedError(error) && error.message.trim()) {
                toast.warning(error.message);
            } else if (!isAxiosError(error) && error instanceof Error && error.message.trim()) {
                toast.error(error.message);
            } else {
                toast.error('Erro ao atribuir formulário. Tente novamente.');
            }
        },
    });

    const selectablePatients = useMemo<AssignPatientListItem[]>(
        () => patients.map((patient) => ({ patient })),
        [patients],
    );
    const isLoadingSelectablePatients =
        isLoadingPatients || (isFetchingPatients && patients.length === 0);

    const handlePatientSelect = (patient: Patient) => {
        setSelectedPatient(patient);
        setStep(2);
    };

    const getDialogTitle = () => {
        if (step === 1) return 'Selecionar paciente';
        if (step === 2) return 'Confirmar envio';
        if (step === 3) return 'Paciente já possui formulário';
        if (step === 4) return 'Formulário pronto';
        return '';
    };

    const getFormattedExpiryDate = () => {
        const data = surveyResponse?.data || surveyResponse;
        if (!data?.unlock_token_expires_at) return '';

        const expiryDate = new Date(data.unlock_token_expires_at);

        return new Intl.DateTimeFormat('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(expiryDate);
    };

    const shareLink = surveyResponse
        ? `${window.location.origin}/patients/survey/answer?token=${
              (surveyResponse?.data || surveyResponse).unlock_token
          }`
        : '';
    const shareText = shareLink ? getSharableSurveyText(shareLink) : '';

    const handleBack = () => {
        if (canSelectPatient) {
            setStep(1);
            return;
        }
        onClose();
    };

    const handleConfirmSend = (confirm = false) => {
        if (sendSurveyAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(sendSurveyAccess.decision));
            return;
        }

        sendSurvey({ confirm });
    };

    const renderContent = () => {
        if (step === 1) {
            return (
                <AssignPatientSelectStep
                    patients={selectablePatients}
                    isLoading={isLoadingSelectablePatients}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onSelectPatient={handlePatientSelect}
                />
            );
        }

        if (step === 2) {
            return (
                <div className="space-y-4">
                    {sendSurveyAccess.shouldBlock && (
                        <Alert variant="warning" className="text-xs">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                                <span>
                                    {getEntitlementBlockedMessage(sendSurveyAccess.decision)}
                                </span>
                                {sendSurveyAccess.isUnavailableInPlan &&
                                sendSurveyAccess.availableFromPlan ? (
                                    <FeatureUnavailableChip
                                        availablePlan={sendSurveyAccess.availableFromPlan}
                                    />
                                ) : null}
                            </div>
                        </Alert>
                    )}
                    <AssignConfirmStep
                        itemLabel={toTitleCase(survey.name)}
                        itemTypeLabel="formulário"
                        patientName={selectedPatient?.full_name}
                        image={PATIENT_FORM_IMAGE}
                    />
                </div>
            );
        }

        if (step === 3) {
            return <AssignSurveyConflictStep patientName={selectedPatient?.full_name} />;
        }

        if (step === 4) {
            return (
                <AssignSuccessStep
                    title="Formulário atribuído com sucesso!"
                    description="O link de acesso foi gerado e está pronto para compartilhamento."
                    expiryDate={getFormattedExpiryDate()}
                    shareLink={shareLink}
                    shareText={shareText}
                />
            );
        }

        return null;
    };

    const content = renderContent();
    const footer = (
        <DialogFooter className="gap-2 sm:space-x-0">
            {step === 1 ? (
                <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
                    Cancelar
                </Button>
            ) : step === 2 ? (
                /* Back lives in the header, so the footer carries only the
                   action that moves the flow forward. */
                <Button
                    onClick={() => handleConfirmSend(false)}
                    disabled={isPending}
                    className="w-full sm:w-auto"
                    aria-disabled={sendSurveyAccess.shouldBlock}
                >
                    {isPending ? 'Gerando...' : 'Confirmar envio'}
                    {sendSurveyAccess.isUnavailableInPlan && sendSurveyAccess.availableFromPlan ? (
                        <FeatureUnavailableChip
                            availablePlan={sendSurveyAccess.availableFromPlan}
                        />
                    ) : null}
                </Button>
            ) : step === 3 ? (
                <Button
                    onClick={() => handleConfirmSend(true)}
                    disabled={isPending}
                    className="w-full sm:w-auto"
                    aria-disabled={sendSurveyAccess.shouldBlock}
                >
                    {isPending ? 'Gerando...' : 'Confirmar e enviar'}
                    {sendSurveyAccess.isUnavailableInPlan && sendSurveyAccess.availableFromPlan ? (
                        <FeatureUnavailableChip
                            availablePlan={sendSurveyAccess.availableFromPlan}
                        />
                    ) : null}
                </Button>
            ) : (
                <Button onClick={onClose} className="w-full">
                    Fechar
                </Button>
            )}
        </DialogFooter>
    );

    // Step 3 is the conflict screen the failed send from step 2 lands on, so its
    // back control returns there rather than leaving the wizard — from step 2
    // the arrow keeps walking outward.
    const backAction =
        step === 2
            ? { label: 'Voltar', onClick: handleBack }
            : step === 3
              ? { label: 'Voltar', onClick: () => setStep(2) }
              : undefined;

    return (
        <AppDialogLayout title={getDialogTitle()} backAction={backAction} footer={footer}>
            {content}
        </AppDialogLayout>
    );
}
